import { Map as MapboxMap, MapLayerMouseEvent } from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { ServiceArea } from '@bringg/types';
import { clustersCountLayerId } from '../../utils/markers-manager';
import { fitBoundsByServiceArea } from '../../mapbox-route-tracker/utils/mapbox-bounds-fit';

const POLYGON_OPACITY = 0.25;

let selectedArea: { featureId: string; serviceArea: ServiceArea } = null;

const colorPalette = [
	'#0075FF',
	'#DB00FF',
	'#FFB800',
	'#E80053',
	'#00CE6B',
	'#000000',
	'#003AAA',
	'#8F00FF',
	'#FF7A00',
	'#FF0000',
	'#00FF0A',
	'#4BE0D7',
	'#FF4B4B',
	'#002366',
	'#FF82E3',
	'#780101',
	'#FBFF46',
	'#8C5400',
	'#590050',
	'#006F12'
];

const NEIGHBOUR_COLOR = '#696969';

const getLabelId = serviceAreaId => {
	return `label${serviceAreaId}`;
};

export const clearSelectedArea = () => {
	selectedArea = null;
};

export const getSelectedArea = () => {
	return selectedArea;
};

export const setSelectedArea = (featureId: string, serviceArea?: ServiceArea) => {
	selectedArea = { featureId, serviceArea };
};

export const removeArea = async (map: MapboxMap, serviceArea: ServiceArea) => {
	if (!serviceArea) return;
	const id = serviceArea.id.toString();
	if (map.getLayer(id)) {
		map.removeLayer(id);
	}
	if (map.getSource(id)) {
		map.removeSource(id);
	}

	const labelId = getLabelId(id);
	if (map.getLayer(labelId)) {
		map.removeLayer(labelId);
	}
	if (map.getSource(labelId)) {
		map.removeSource(labelId);
	}
};

export const getServiceAreaColor = (serviceArea: ServiceArea, teamId?: number) => {
	const index = serviceArea.position % colorPalette.length;
	return !teamId || teamId === serviceArea.team_id ? colorPalette[index] : NEIGHBOUR_COLOR;
};

export const showAreas = (
	map: MapboxMap,
	draw: MapboxDraw,
	serviceAreas: ServiceArea[],
	editable: boolean,
	shouldFitBounds: boolean,
	teamId: number
) => {
	serviceAreas.forEach(serviceArea => {
		showArea({ map, draw, serviceArea, editable, teamId });
	});
	if (shouldFitBounds) {
		fitBoundsByServiceArea(map, serviceAreas);
	}
};

interface ServiceAreaDrawProps {
	map: MapboxMap;
	draw;
	serviceArea: ServiceArea;
	editable: boolean;
	teamId: number;
}

export const showArea = (props: ServiceAreaDrawProps) => {
	showPolygon(props);
};

export const showPolygon = ({ map, draw, serviceArea, editable, teamId }: ServiceAreaDrawProps) => {
	if (!map) return;
	if (!draw) return;
	const id = serviceArea.id.toString();
	if (map.getLayer(id)) {
		map.removeLayer(id);
	}
	if (map.getSource(id)) {
		map.removeSource(id);
	}
	const userGeoData = polygonGeoData(serviceArea);

	map.addSource(id, {
		type: 'geojson',
		data: userGeoData
	});

	map.addLayer({
		id: id,
		type: 'fill',
		source: id,
		layout: {},
		paint: {
			'fill-color': getServiceAreaColor(serviceArea, teamId),
			'fill-opacity': POLYGON_OPACITY
		}
	});

	map.on('click', id, (event: MapLayerMouseEvent) => {
		event.preventDefault();
		if (!event) {
			return;
		}
		onAreaClicked(serviceArea, draw, userGeoData, editable, teamId);
	});

	showPolygonName(map, serviceArea);
};

const showPolygonName = (map: MapboxMap, serviceArea: ServiceArea) => {
	const labelId = getLabelId(serviceArea.id);
	if (map.getLayer(labelId)) {
		map.removeLayer(labelId);
	}
	if (map.getSource(labelId)) {
		map.removeSource(labelId);
	}
	map.addSource(labelId, {
		type: 'geojson',
		data: {
			type: 'Feature',
			properties: {
				description: serviceArea.name
			},
			geometry: {
				type: 'Point',
				coordinates: getPolygonCentroid(serviceArea.polygon)
			}
		}
	});

	const clusterCountLayer = map.getLayer(clustersCountLayerId);

	map.addLayer(
		{
			id: labelId,
			type: 'symbol',
			source: labelId,
			layout: {
				'text-field': ['format', ['get', 'description'], { 'text-font': ['literal', ['Rubik Regular']] }],
				'text-size': 14
			},
			paint: {
				'text-color': '#242424',
				'text-halo-color': 'white',
				'text-halo-width': 1
			}
		},
		clusterCountLayer?.id
	);
};

const onAreaClicked = (serviceArea: ServiceArea, draw: MapboxDraw, userGeoData, editable: boolean, teamId: number) => {
	if (!editable || teamId !== serviceArea.team_id) {
		return;
	}
	onClearSelection(draw);
	const featureIds = draw.add(userGeoData);
	const featureId = featureIds[0];
	selectedArea = { featureId, serviceArea };
	draw.changeMode('simple_select', { featureIds: [featureId] });
};

const onClearSelection = (draw: MapboxDraw) => {
	draw.deleteAll();
	clearSelectedArea();
};

const polygonGeoData = (serviceArea: ServiceArea): GeoJSON.Feature<GeoJSON.Polygon> => {
	return {
		type: 'Feature',
		properties: {
			description: serviceArea.name
		},
		geometry: {
			type: 'Polygon',
			coordinates: [serviceArea.polygon]
		}
	};
};

const getPolygonCentroid = (polygon: number[][]) => {
	var x = polygon.map(xy => xy[0]);
	var y = polygon.map(xy => xy[1]);
	var cx = (Math.min(...x) + Math.max(...x)) / 2;
	var cy = (Math.min(...y) + Math.max(...y)) / 2;
	return [cx, cy];
};
